import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DAYS_OF_WEEK,
  PATTERN_TYPES,
  BOOKING_DIRECTIONS,
  patternDefaults,
} from "@/lib/constants";
import {
  createRecurringPattern,
  listBaysLite,
  listSitesLite,
  updateRecurringPattern,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PatternAttachmentsPanel from "@/components/PatternAttachmentsPanel";
import PatternClashPreview from "@/components/PatternClashPreview";
import HelpTip from "@/components/HelpTip";
import { AuDateInput, AuTimeInput } from "@/components/AuDateTime";

export default function RecurringPatternForm({ open, onOpenChange, pattern, defaultSiteId, onSaved }) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="pattern-form-dialog">
        <Inner
          key={pattern?.id || "new"}
          pattern={pattern}
          defaultSiteId={defaultSiteId}
          onSaved={(row) => {
            onSaved?.(row);
            onOpenChange(false);
          }}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function Inner({ pattern, defaultSiteId, onSaved, onCancel }) {
  const { user } = useAuth();
  const isEdit = !!pattern?.id;
  const [busy, setBusy] = useState(false);
  const [sites, setSites] = useState([]);
  const [bays, setBays] = useState([]);
  const [noEnd, setNoEnd] = useState(() => !pattern?.end_date);

  const [v, setV] = useState(() => {
    if (!pattern) {
      const base = patternDefaults();
      return defaultSiteId ? { ...base, site_id: defaultSiteId } : base;
    }
    return {
      site_id: pattern.site_id || "",
      bay_id: pattern.bay_id || "",
      pattern_type: pattern.pattern_type || "weekly",
      day_of_week: pattern.day_of_week || [],
      time_start: (pattern.time_start || "08:00").slice(0, 5),
      time_end: (pattern.time_end || "09:00").slice(0, 5),
      start_date: pattern.start_date || new Date().toISOString().slice(0, 10),
      end_date: pattern.end_date || "",
      booking_template: pattern.booking_template || {},
      requires_approval: pattern.requires_approval ?? true,
      is_active: pattern.is_active ?? true,
      client_id: pattern.client_id,
    };
  });

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const setTpl = (k, val) =>
    setV((s) => ({ ...s, booking_template: { ...s.booking_template, [k]: val } }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listSitesLite();
      if (cancelled) return;
      setSites(data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!v.site_id) return;
    (async () => {
      const { data } = await listBaysLite(v.site_id, { includeInactive: true });
      if (cancelled) return;
      setBays(data || []);
    })();
    return () => { cancelled = true; };
  }, [v.site_id]);

  // Reset bays list when site is cleared (handler-driven, not effect-driven)

  const toggleDay = (d) => {
    setV((s) => {
      const has = s.day_of_week.includes(d);
      return {
        ...s,
        day_of_week: has ? s.day_of_week.filter((x) => x !== d) : [...s.day_of_week, d].sort(),
      };
    });
  };

  const submit = async () => {
    if (!v.site_id || !v.bay_id) {
      toast.error("Site and bay are required.");
      return;
    }
    if (v.pattern_type === "weekly" || v.pattern_type === "fortnightly") {
      if (!v.day_of_week.length) {
        toast.error("Pick at least one day of the week.");
        return;
      }
    }
    if (v.time_end <= v.time_start) {
      toast.error("End time must be after start time.");
      return;
    }
    setBusy(true);
    const { data, error } = isEdit
      ? await updateRecurringPattern(pattern.id, v)
      : await createRecurringPattern(v, user?.id);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(isEdit ? "Pattern updated." : "Pattern created.");
    onSaved?.(data);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle data-testid="pattern-form-title">
          {isEdit ? "Edit recurring pattern" : "New recurring pattern"}
        </DialogTitle>
        <DialogDescription>
          Patterns auto-generate bookings on the chosen days. Toggle <strong>Active</strong> off
          to hold the pattern &mdash; held patterns show as ghosted previews on the dock grid.
        </DialogDescription>
      </DialogHeader>

      <Tabs defaultValue="schedule" className="mt-2">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="schedule" data-testid="pattern-tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="template" data-testid="pattern-tab-template">Booking template</TabsTrigger>
          <TabsTrigger value="docs" data-testid="pattern-tab-docs" disabled={!isEdit}>
            Documents
          </TabsTrigger>
          <TabsTrigger value="preview" data-testid="pattern-tab-preview">Clash preview</TabsTrigger>
        </TabsList>

        <TabsContent value="schedule" className="space-y-4 pt-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Site *">
              <Select value={v.site_id} onValueChange={(x) => { set("site_id", x); set("bay_id", ""); setBays([]); }}>
                <SelectTrigger data-testid="pattern-site"><SelectValue placeholder="Pick a site" /></SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} {s.code ? `· ${s.code}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Bay *">
              <Select value={v.bay_id} onValueChange={(x) => set("bay_id", x)} disabled={!v.site_id}>
                <SelectTrigger data-testid="pattern-bay"><SelectValue placeholder="Pick a bay" /></SelectTrigger>
                <SelectContent>
                  {bays.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name} {b.code ? `· ${b.code}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Pattern type *" hint="Daily runs every day between start and end date. Weekly repeats on the chosen days each week. Fortnightly runs the chosen days every second week starting from the start date.">
              <Select value={v.pattern_type} onValueChange={(x) => set("pattern_type", x)}>
                <SelectTrigger data-testid="pattern-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PATTERN_TYPES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div />
          </div>

          {(v.pattern_type === "weekly" || v.pattern_type === "fortnightly") && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label>Days of week</Label>
                <HelpTip id="pattern-days">
                  Pick every weekday the pattern should run. Public holidays and site blackouts will
                  automatically skip generation on those dates.
                </HelpTip>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="pattern-days">
                {DAYS_OF_WEEK.map((d) => {
                  const on = v.day_of_week.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => toggleDay(d.value)}
                      data-testid={`pattern-day-${d.value}`}
                      className={`px-3 py-1.5 text-xs rounded border transition ${
                        on
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-4 gap-3">
            <Field label="Start time *">
              <AuTimeInput
                value={v.time_start}
                onChange={(e) => set("time_start", e.target.value)}
                data-testid="pattern-time-start"
              />
            </Field>
            <Field label="End time *">
              <AuTimeInput
                value={v.time_end}
                onChange={(e) => set("time_end", e.target.value)}
                data-testid="pattern-time-end"
              />
            </Field>
            <Field label="Start date *">
              <AuDateInput
                value={v.start_date}
                onChange={(e) => set("start_date", e.target.value)}
                data-testid="pattern-start-date" required
              />
            </Field>
            <Field label="End date" hint="Leave blank (or tick 'No end date') to run indefinitely. Set an end date if the customer has a contracted finish, or if you want to review before rolling into a new period.">
              <div className="space-y-1.5">
                <AuDateInput
                  value={noEnd ? "" : v.end_date}
                  onChange={(e) => set("end_date", e.target.value)}
                  disabled={noEnd}
                  data-testid="pattern-end-date"
                />
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={noEnd}
                    onChange={(e) => {
                      setNoEnd(e.target.checked);
                      if (e.target.checked) set("end_date", "");
                    }}
                    data-testid="pattern-no-end"
                  />
                  No end date (runs indefinitely)
                </label>
              </div>
            </Field>
          </div>

          <div className="flex items-center gap-6 pt-1">
            <div className="flex items-center gap-2">
              <Switch
                checked={v.is_active}
                onCheckedChange={(x) => set("is_active", x)}
                data-testid="pattern-is-active"
              />
              <Label className="text-sm">
                Active {v.is_active ? "" : "(held)"}
              </Label>
              <HelpTip id="pattern-active">
                When held, no new bookings are generated and the pattern shows as ghosted previews on
                the dock grid. Existing bookings already generated are not deleted.
              </HelpTip>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={v.requires_approval}
                onCheckedChange={(x) => set("requires_approval", x)}
                data-testid="pattern-requires-approval"
              />
              <Label className="text-sm">Requires approval</Label>
              <HelpTip id="pattern-approval">
                Generated bookings arrive in Pending status and need a site admin to Accept them
                before the driver is confirmed. Leave off for trusted contracted lanes.
              </HelpTip>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="template" className="space-y-3 pt-3">
          <p className="text-xs text-slate-500">
            These fields seed every generated booking. They can still be edited per-booking afterwards.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Direction">
              <Select
                value={v.booking_template.direction || "inbound"}
                onValueChange={(x) => setTpl("direction", x)}
              >
                <SelectTrigger data-testid="tpl-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BOOKING_DIRECTIONS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Vehicle type">
              <Input
                value={v.booking_template.vehicle_type || ""}
                onChange={(e) => setTpl("vehicle_type", e.target.value)}
                data-testid="tpl-vehicle-type"
                placeholder="B-double, semi, rigid…"
              />
            </Field>
            <Field label="Sender">
              <Input
                value={v.booking_template.sender_name || ""}
                onChange={(e) => setTpl("sender_name", e.target.value)}
                data-testid="tpl-sender"
              />
            </Field>
            <Field label="Receiver">
              <Input
                value={v.booking_template.receiver_name || ""}
                onChange={(e) => setTpl("receiver_name", e.target.value)}
                data-testid="tpl-receiver"
              />
            </Field>
          </div>
          <Field label="Goods description">
            <Textarea
              rows={2}
              value={v.booking_template.goods_description || ""}
              onChange={(e) => setTpl("goods_description", e.target.value)}
              data-testid="tpl-goods"
            />
          </Field>
          <Field label="Notes">
            <Textarea
              rows={2}
              value={v.booking_template.notes || ""}
              onChange={(e) => setTpl("notes", e.target.value)}
              data-testid="tpl-notes"
            />
          </Field>
        </TabsContent>

        <TabsContent value="docs" className="pt-3">
          {isEdit ? (
            <PatternAttachmentsPanel pattern={pattern} />
          ) : (
            <p className="text-sm text-slate-500">
              Save the pattern first to attach documents.
            </p>
          )}
        </TabsContent>

        <TabsContent value="preview" className="pt-3">
          <PatternClashPreview values={v} bays={bays} />
        </TabsContent>
      </Tabs>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onCancel} data-testid="pattern-cancel">
          Cancel
        </Button>
        <Button onClick={submit} disabled={busy} data-testid="pattern-submit">
          {busy ? "Saving…" : isEdit ? "Save changes" : "Create pattern"}
        </Button>
      </DialogFooter>
    </>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
        {hint ? <HelpTip id={typeof label === "string" ? label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") : undefined}>{hint}</HelpTip> : null}
      </div>
      {children}
    </div>
  );
}
